<template>
  <div class="directory-detail clearfix">
    <TMSHeader
      :title="title"
      :buttons="buttons"
      :dotOption="dotOption"
      :fullScreen="true"
    >
      <div
        slot="status"
        :class="pointerClass"
        @click="openLifecycleFun"
      >
        <TheDirStatusHeader />
      </div>
    </TMSHeader>
    <Lifecycle
      v-if="isEngineer"
      ref="directoryLifecycle"
      :modalId="'directory-modules-lifecycle-modal'"
      :type="'directory'"
      :commentAttr="lifecycleCommentAttrs.comments"
      :histories="histories"
      :allowPreformattedComments="true"
      :emptyHistoryHint="emptyHistoryHint"
    />
    <div class="directory-detail__info clearfix">
      <div class="effective--date-reject-history">
        <div :class="[isFloorDR ? 'floor-effective-remove-date': 'effective-remove-date']">
          <div
            class="effective-date"
            v-if="showRemoveDate"
          >
            <label class="emphasis required-flag">{{ $t('directory.removeDate') }}</label>
            <span class="hint">{{ $t('directory.msg.subjectTo') }}</span>
            <DatePicker
              id="directory-effective-date"
              :required="true"
              :class="{ 'error-highlight': invalidDate }"
              :calendarButton="true"
              calendarButtonIcon="fa fa-calendar"
              :disabled="{ to: today }"
              :disabledPicker="!canEditRemoveDate"
              :value="currentDirectory.removeDate | formatDate"
              @selected="val => _updateField(['removeDate'], val)"
              @cleared="_updateField(['removeDate'], null)"
            />
          </div>
          <div
            class="effective-date"
            v-if="!showRemoveDate"
          >
            <label class="emphasis required-flag">{{ $t('directory.effectiveDate') }}</label>
            <span class="hint">{{ $t('directory.msg.subjectTo') }}</span>
            <DatePicker
              id="directory-effective-date"
              :required="true"
              :class="{ 'error-highlight': invalidDate }"
              :calendarButton="true"
              calendarButtonIcon="fa fa-calendar"
              :disabled="{ to: today }"
              :disabledPicker="!editableEffectiveDate"
              :value="currentDirectory.effectiveDate | formatDate"
              @selected="val => _updateField(['effectiveDate'], val)"
              @cleared="_updateField(['effectiveDate'], null)"
            />
          </div>
          <div
            v-if="isFloorDR"
            class="floor"
          >
            <label class="emphasis required-flag">{{ $t('directory.label.floor') }}</label>
            <span class="hint">{{ $t('directory.msg.selectFloor') }}</span>
            <HKLSelect
              :options="floorOptions"
              placeholder=""
              :showSearch="true"
              :class="{ 'error-highlight': invalidFloor }"
              :value="currentDirectory.floor"
              :required="isFloorDR"
              :disabled="!editableEffectiveDate"
              @change="val => _updateField(['floor'], val)"
            />
          </div>
        </div>
        <div
          class="guideline"
          :class="['guideline', isFloorDR ? 'floor-guideline': '']"
        >
          <div class="emphasis guideline-title">{{ $t(`termsAndConditions.${regionName}.directoryGuideLine.title`) }}</div>
          <ol>
            <li
              v-for="(item, key) in $t(`termsAndConditions.${regionName}.directoryGuideLine.content`)"
              :key="key"
            >{{ item }}</li>
          </ol>
        </div>
      </div>
      <div
        class="reject-history"
        v-if="latestRejectedHistory"
      >
        <label class="emphasis">{{ $t('directory.label.commentsFromOn',[latestRejectedHistory.who, latestRejectedHistory.when]) }}</label>
        <div class="reject-history__item">
          <div class="reject-comment">
            <pre>{{ latestRejectedHistory.comment }}</pre>
          </div>
        </div>
      </div>
      <template>
        <div
          class="row"
          v-if="companies.length"
        >
          <div class="small-6 large-6 columns detail">
            <template v-for="(company, index) in companies">
              <Company
                :id="company.id"
                :index="index"
                :key="company.id"
                :company.sync="company"
                :type="currentDirectory.type"
                @reset:validation="resetValidation"
              />
            </template>
          </div>
          <div class="small-6 large-6 preview">
            <img :src="directoryImg">
            <UploadDocs />
          </div>
        </div>
      </template>
    </div>

    <ActionConfirmModal
      :modalId="'confirm-' + currentDirectory.id"
      ref="confirmSubmit"
      :confirmTitle="confirmTitle"
    />

  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'

import ActionConfirmModal from '@/controls/ActionConfirmModal'
import Company from './Company'
import DatePicker from '@/controls/DatePicker'
import Lifecycle from '@/modules/Lifecycle'
import HKLSelect from '@/controls/Select/HKLSelect'
import UploadDocs from './UploadDocs'
import TMSHeader from '@/controls/TMSHeader'
import TheDirStatusHeader from './TheDirStatusHeader'

import { deleteAllDocuments } from '@/api/docs/request'
import { getDirectory } from '@/api/directories/request'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import { directoryRouteNames, lifecycleCommentAttrs, ActionTypes, EmailTypes } from '@/utils/consts'
import { DIR_STATUS, DIR_ACTION, DEFAULT_SOURCEID, tenantCreatorEditableStatus, hkldCreatorEditableStatus, creatorEditableStatus } from './consts'
import { isDuplicatedDirectory, deleteDocsAfterDate, getCancellable } from './utils'
import { sendEmail } from '@/api/postman/request'
import directoryDetailMixin from './directoryDetailMixin'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'

export default {
  name: 'DirectoryEdit',
  components: {
    TheDirStatusHeader,
    ActionConfirmModal,
    Company,
    DatePicker,
    HKLSelect,
    TMSHeader,
    Lifecycle,
    UploadDocs,
  },
  mixins: [directoryDetailMixin],
  data () {
    const today = new Date()

    return {
      today,
      confirmTitle: '',
      invalidDate: false,
      invalidFloor: false,
      noCompany: false,
      noAttachment: false,
      lifecycleCommentAttrs,
      DIR_STATUS,
      DIR_ACTION,
      emptyHistoryHint: this.$t('hint.emptyDirectoryLifecycle'),
    }
  },

  methods: {
    ...mapActions('directories', ['loadDirectory', 'updateDirectory', 'submitDirectory', 'deleteDirectory']),
    ...mapMutations('directories', {
      updateField: mutationTypes.DIRECTORY_SET_FIELD_BY_PATH,
      updateCompany: mutationTypes.DIRECTORY_UPDATE_COMPANY,
    }),

    openConfirmModal (func) {
      this.$refs['confirmSubmit'].open(func)
    },

    confirmCancel () {
      this.confirmTitle = this.$t(`directory.hint.cancelRequest`)
      if (this.currentDirectory.action === DIR_ACTION.REMOVE || this.currentDirectory.sourceId !== DEFAULT_SOURCEID) {
        this.confirmTitle = this.$t(`directory.hint.cancelRequest-${DIR_ACTION.REMOVE}`)
      }
      this.openConfirmModal(this.cancelDirectory)
    },

    confirmSubmit () {
      this.openConfirmModal(this.submit)
    },

    _updateField (path, value) {
      this.updateField({ path, value })
      formUtils.setDirty()
    },

    resetValidation () {
      fusionInputCheckUtil.resetAllFields()
      this.invalidDate = false
      this.invalidFloor = false
    },

    async validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.detail')
      const date = this.canEditRemoveDate ? this.currentDirectory.removeDate : this.currentDirectory.effectiveDate
      this.invalidDate = new Date(date) <= this.today
      this.invalidFloor = this.isFloorDR && !this.currentDirectory.floor
      if (this.companies.length === 0) {
        this.noCompany = true
        notification.fireNotification(false, this.$t('directory.msg.noCompany'))
      }
      if (this.invalidDate) {
        notification.fireNotification(false, this.$t('directory.msg.invalidDate'))
      }
      return fusionInputCheckUtil.isAllFieldsValid('.detail') &&
        !this.invalidDate && !this.invalidFloor && !this.noCompany
    },

    async cancelDirectory () {
      const item = this.currentDirectory
      let isDuplicated = false
      let rollbackItem = item
      if (item.sourceId !== DEFAULT_SOURCEID) {
        const sourceItem = getDirectory(item.sourceId)
        isDuplicated = isDuplicatedDirectory(item, sourceItem)
        if (isDuplicated) {
          rollbackItem = sourceItem
        }
      }
      if (item.action === DIR_ACTION.ADD) {
        await this.deleteDirectory({
          id: item.id,
        })
        await deleteAllDocuments(item.id)
      }
      const rollbackId = isDuplicated ? item.sourceId : item.id
      if (isDuplicated || item.action === DIR_ACTION.REMOVE) {
        const request = lodash.cloneDeep(rollbackItem)
        request.removeDate = null
        request.specificAttrs = true
        await this.submitDirectory({
          id: rollbackId,
          action: DIR_ACTION.ADD,
          status: DIR_STATUS.PUBLISHED,
          needUpdate: true,
          refresh: false,
          request,
        })
        if (this.publishedAt) {
          await deleteDocsAfterDate(rollbackId, this.publishedAt)
        }
      }
      formUtils.resetDirty()
      navigation.toPage(this.isTenant ? directoryRouteNames.tenantDirectories : directoryRouteNames.tenancyDirectories, { id: this.currentDirectory.tenancyId })
    },

    async discardChange () {
      await this.loadDirectory()
      formUtils.resetDirty()
    },

    async save () {
      if (this.editableEffectiveDate && this.currentDirectory.status !== DIR_STATUS.DRAFT && this.currentDirectory.status !== DIR_STATUS.REJECTED && !await this.validate()) {
        return
      }
      let request = this.currentDirectory
      request.specificAttrs = true
      if (this.currentDirectory.status === DIR_STATUS.REJECTED && this.currentDirectory.action !== DIR_ACTION.REMOVE) {
        request.status = DIR_STATUS.DRAFT
      }
      if ((this.currentDirectory.status === DIR_STATUS.SUBMITTED || this.currentDirectory.status === DIR_STATUS.APPR) && this.currentDirectory.action !== DIR_ACTION.REMOVE) {
        request.isSaved = true
      }
      await this.updateDirectory({ request, headerAction: ActionTypes.directories.saved })
      this.resetValidation()
      formUtils.resetDirty()
      if (![DIR_STATUS.DRAFT, DIR_STATUS.REJECTED].includes(this.currentDirectory.status)) {
        navigation.toPage(
          this.isTenant ? directoryRouteNames.tenantDirectoryDetailView : directoryRouteNames.directoryDetailView,
          { id: this.$route.params.id },
        )
      }
    },

    async submit () {
      this.resetValidation()
      if (!await this.validate()) {
        return
      }
      let status = this.canCurrUserCreate && !this.canCurrUserApprove ? DIR_STATUS.CART : DIR_STATUS.SUBMITTED
      let action = this.isRemoveAction ? { action: DIR_ACTION.REMOVE } : {}
      await this.submitDirectory({
        status,
        ...action,
        headerAction: status === DIR_STATUS.CART ? undefined : ActionTypes.directories.submitted,
      })
      if (status === DIR_STATUS.SUBMITTED) {
        await sendEmail(EmailTypes.directories.waitForApproval, [this.currentDirectory])
        await sendEmail(EmailTypes.directories.submitted, [this.currentDirectory])
      }
      this.resetValidation()
      formUtils.resetDirty()
      navigation.toPage(this.isTenant ? directoryRouteNames.tenantDirectories : directoryRouteNames.tenancyDirectories, { id: this.currentDirectory.tenancyId })
    },
  },

  mounted () {
    if (this.$router.currentRoute.query.removeAndDuplicate) {
      notification.fireNotification(true, this.$t('directory.hint.removeAndDuplicate'))
    }
  },

  computed: {
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    ...mapGetters('form', ['isPageDirty']),
    ...mapGetters('directories', ['currentDirectory', 'tenancyInfo', 'canCurrUserApprove', 'canCurrUserCreate']),
    ...mapGetters('regions', ['currentRegion']),
    regionName () {
      return this.currentRegion.name
    },
    buttons () {
      let buttons = []
      if (this.canCompInfoEditable || this.editableEffectiveDate || this.isSimplifiedChineseEditable || this.editableRemoveDateWithoutStatusChange) {
        buttons = [{
          title: this.$t('action.discard'),
          func: this.discardChange,
          disable: !this.isPageDirty,
          cls: 'btn--darkblue',
        }, {
          title: this.$t('action.save'),
          func: this.save,
          cls: 'btn--darkblue',
        }]
      }

      if (this.canCompInfoEditable || this.editableRemoveDate) {
        let submitBtnTitle = this.canCurrUserCreate && !this.canCurrUserApprove ? 'directory.action.AddToCart' : 'action.submit'
        buttons.push({
          title: this.$t(submitBtnTitle),
          func: this.submit,
          cls: 'btn--orange',
        })
      }
      return buttons
    },
    canCancel () {
      return getCancellable({
        canCurrUserCreate: this.canCurrUserCreate,
        directory: this.currentDirectory,
        isTenant: this.isTenant,
        isEngineer: this.isEngineer,
      })
    },
    dotOption () {
      let options = []
      if (this.canCancel) {
        options.push({
          title: this.$t('directory.action.cancelRequest'),
          operate: this.confirmCancel,
        })
      }
      return options
    },

    floorOptions () {
      return this.tenancyInfo.floors || []
    },
    editableEffectiveDate () {
      let editableStatus = []
      if (this.isTenant) {
        editableStatus = tenantCreatorEditableStatus
      } else if (this.canCurrUserCreate && !this.isTenant) {
        editableStatus = creatorEditableStatus
      }
      return this.currentDirectory.action === DIR_ACTION.ADD && editableStatus.includes(this.currentDirectory.status)
    },
    canEditRemoveDate () {
      return this.editableRemoveDate || this.editableRemoveDateWithoutStatusChange
    },
    editableRemoveDateWithoutStatusChange () {
      const editableStatus = [DIR_STATUS.SUBMITTED, DIR_STATUS.APPR]
      return this.canCurrUserCreate && editableStatus.includes(this.currentDirectory.status) && this.currentDirectory.action === DIR_ACTION.REMOVE
    },
    editableRemoveDate () {
      let editableStatus = []
      if (this.isTenant || this.canCurrUserCreate) {
        editableStatus = tenantCreatorEditableStatus
      }
      return (editableStatus.includes(this.currentDirectory.status) && this.currentDirectory.action === DIR_ACTION.REMOVE) ||
        (this.isRemoveAction && this.currentDirectory.status === DIR_STATUS.PUBLISHED && this.currentDirectory.action === DIR_ACTION.ADD)
    },
    showRemoveDate () {
      return this.isRemoveAction || this.currentDirectory.action === DIR_ACTION.REMOVE
    },
    isRemoveAction () {
      const isRemoveAction = [directoryRouteNames.tenantDirectories, directoryRouteNames.tenancyDirectories].includes(this.$router.currentRoute.query.fromPage)
      return (isRemoveAction && DIR_STATUS.PUBLISHED === this.currentDirectory.status && DIR_ACTION.ADD === this.currentDirectory.action)
    },
    canCompInfoEditable () {
      return this.canCurrUserCreate &&
        this.currentDirectory.action !== DIR_ACTION.REMOVE && tenantCreatorEditableStatus.includes(this.currentDirectory.status)
    },
    isSimplifiedChineseEditable () {
      return this.canCurrUserCreate && this.isEngineer &&
        this.currentDirectory.action !== DIR_ACTION.REMOVE && hkldCreatorEditableStatus.includes(this.currentDirectory.status)
    },
    publishedAt () {
      return lodash.get(this.currentDirectory, 'publishedAt')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/directories/detail';
</style>
